import React, { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import * as CANNON from "cannon-es";
import { Box, Button, Typography, LinearProgress } from "@mui/material";
import setupScene from "./FlowerShooterGame/SceneSetup";
import PlayerController from "./FlowerShooterGame/PlayerController";
import WeaponSystem, { MAG_SIZE } from "./FlowerShooterGame/WeaponSystem";
import FlowerSpawner from "./FlowerShooterGame/FlowerSpawner";
import BotsController from "./FlowerShooterGame/BotsController";
import SoundKit from "./FlowerShooterGame/SoundKit";

const PLAYER_RADIUS = 0.45;
const BLOOM_MAX = 100;
const HIT_TO_BLOOM = 20;
const INVULN_TIME = 2;

const FlowerShooterGame = ({
  mapName = "zen-garden",
  maxPlayers = 8,
  soloMode = true,
  height = "100vh",
}) => {
  const containerRef = useRef(null);
  const gameRef = useRef(null);
  const statusRef = useRef("menu");
  const bloomRef = useRef(0);
  const invulnRef = useRef(0);
  const flashTimerRef = useRef(null);

  const [status, setStatus] = useState("menu");
  const [hud, setHud] = useState({
    ammo: MAG_SIZE,
    reloading: false,
    bloom: 0,
    score: 0,
    flowers: 0,
  });
  const [bloomingFlash, setBloomingFlash] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    statusRef.current = "menu";
    bloomRef.current = 0;
    invulnRef.current = 0;
    setHud({ ammo: MAG_SIZE, reloading: false, bloom: 0, score: 0, flowers: 0 });

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.1;
    Object.assign(renderer.domElement.style, {
      display: "block",
      width: "100%",
      height: "100%",
      outline: "none",
    });
    container.appendChild(renderer.domElement);

    const resize = () => {
      const { clientWidth, clientHeight } = container;
      if (clientWidth === 0 || clientHeight === 0) return;
      renderer.setSize(clientWidth, clientHeight);
      camera.aspect = clientWidth / clientHeight;
      camera.updateProjectionMatrix();
    };
    const resizeObserver = new ResizeObserver(resize);

    const env = setupScene({ mapName });
    const {
      scene,
      camera,
      world,
      playerMaterial,
      collidables,
      waypoints,
      spawnPoints,
      exposure,
      mapInfo,
    } = env;
    renderer.toneMappingExposure = exposure;

    const sound = new SoundKit();

    const spawner = new FlowerSpawner({
      scene,
      onPlanted: (total) => {
        setHud((prev) => (prev.flowers === total ? prev : { ...prev, flowers: total }));
      },
    });

    const spawnBody = new CANNON.Body({
      mass: 5,
      material: playerMaterial,
      shape: new CANNON.Sphere(PLAYER_RADIUS),
      position: new CANNON.Vec3(
        spawnPoints[0].x,
        spawnPoints[0].y + PLAYER_RADIUS,
        spawnPoints[0].z
      ),
      linearDamping: 0,
      fixedRotation: true,
    });
    spawnBody.updateMassProperties();
    world.addBody(spawnBody);

    const player = new PlayerController({ camera, body: spawnBody, collidables });
    player.lookAtFrom(spawnPoints[0]);

    const botCount = Math.max(1, Math.min(3, maxPlayers - 1));
    let bots = null;
    if (soloMode) {
      bots = new BotsController({
        scene,
        world,
        collidables,
        waypoints,
        spawnPoints,
        flowerSpawner: spawner,
        getPlayerPosition: () =>
          new THREE.Vector3(
            spawnBody.position.x,
            spawnBody.position.y,
            spawnBody.position.z
          ),
        onPlayerHit: (damage) => applyPlayerHit(damage),
        onBotBloomed: () => {
          sound.botBlooms();
          setHud((prev) => ({ ...prev, score: prev.score + 1 }));
        },
        count: botCount,
      });
    }

    const weapon = new WeaponSystem({
      camera,
      collidables,
      flowerSpawner: spawner,
      bots,
      onAmmoChange: (ammo, reloading) =>
        setHud((prev) =>
          prev.ammo === ammo && prev.reloading === reloading
            ? prev
            : { ...prev, ammo, reloading }
        ),
      onShot: () => {
        player.applyRecoil();
        sound.shot();
      },
      onEmptyClick: () => sound.emptyClick(),
    });

    function applyPlayerHit(damage) {
      if (statusRef.current !== "playing" || invulnRef.current > 0) return;
      sound.hurt();
      bloomRef.current = Math.min(BLOOM_MAX, bloomRef.current + damage);
      setHud((prev) => ({ ...prev, bloom: bloomRef.current }));
      if (bloomRef.current >= BLOOM_MAX) {
        respawnAfterBloom();
      }
    }

    function respawnAfterBloom() {
      const feet = new THREE.Vector3(
        spawnBody.position.x,
        0.1,
        spawnBody.position.z
      );
      spawner.spawnBurst(feet, 8);
      sound.playerBlooms();
      let farthest = spawnPoints[0];
      let bestDist = -1;
      spawnPoints.forEach((sp) => {
        if (bots) {
          let minToBot = Infinity;
          for (const bot of bots.bots) {
            if (bot.dead) continue;
            const d = bot.body.position;
            minToBot = Math.min(minToBot, (sp.x - d.x) ** 2 + (sp.z - d.z) ** 2);
          }
          if (minToBot > bestDist) {
            bestDist = minToBot;
            farthest = sp;
          }
        } else if (sp.distanceToSquared(feet) > bestDist) {
          bestDist = sp.distanceToSquared(feet);
          farthest = sp;
        }
      });
      player.lookAtFrom(farthest);
      bloomRef.current = 0;
      invulnRef.current = INVULN_TIME;
      setHud((prev) => ({ ...prev, bloom: 0 }));
      setBloomingFlash(true);
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
      flashTimerRef.current = setTimeout(() => setBloomingFlash(false), 900);
    }

    resize();
    resizeObserver.observe(container);

    const onPointerLockChange = () => {
      const locked = document.pointerLockElement === container;
      statusRef.current = locked ? "playing" : "paused";
      player.enabled = locked;
      setStatus(statusRef.current);
      if (!locked) weapon.setFiring(false);
    };

    const onMouseMove = (event) => {
      if (document.pointerLockElement !== container) return;
      player.handleMouseMove(event.movementX, event.movementY);
    };

    const onMouseDown = (event) => {
      if (document.pointerLockElement !== container || event.button !== 0) return;
      weapon.setFiring(true);
    };

    const onMouseUp = (event) => {
      if (event.button !== 0) return;
      weapon.setFiring(false);
    };

    const onKeyDown = (event) => {
      if (statusRef.current !== "playing") return;
      if (event.code === "KeyR") weapon.requestReload();
    };

    document.addEventListener("pointerlockchange", onPointerLockChange);
    document.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mousedown", onMouseDown);
    window.addEventListener("mouseup", onMouseUp);
    window.addEventListener("keydown", onKeyDown);

    let rafId = 0;
    let lastTime = performance.now();
    let ambientElapsed = 0;

    const tick = (now) => {
      rafId = requestAnimationFrame(tick);
      const dt = Math.min((now - lastTime) / 1000, 0.05);
      lastTime = now;
      ambientElapsed += dt;

      if (statusRef.current === "playing") {
        if (invulnRef.current > 0) invulnRef.current -= dt;
        world.step(1 / 60, dt, 4);
        player.update(dt, ambientElapsed);
        weapon.update(dt, ambientElapsed, player.yaw, player.pitch);
        if (bots) bots.update(dt, ambientElapsed);
        spawner.update(dt, ambientElapsed);
      } else {
        spawner.update(dt, ambientElapsed);
      }

      renderer.render(scene, camera);
    };
    rafId = requestAnimationFrame(tick);

    gameRef.current = {
      sound,
      mapInfo,
      dispose: () => {
        cancelAnimationFrame(rafId);
        resizeObserver.disconnect();
        document.removeEventListener("pointerlockchange", onPointerLockChange);
        document.removeEventListener("mousemove", onMouseMove);
        window.removeEventListener("mousedown", onMouseDown);
        window.removeEventListener("mouseup", onMouseUp);
        window.removeEventListener("keydown", onKeyDown);
        if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
        if (document.pointerLockElement === container) document.exitPointerLock();
        weapon.dispose();
        if (bots) bots.dispose();
        player.dispose();
        spawner.dispose();
        sound.dispose();
        world.removeBody(spawnBody);
        env.dispose();
        renderer.dispose();
        if (renderer.domElement.parentNode === container) {
          container.removeChild(renderer.domElement);
        }
        gameRef.current = null;
      },
    };

    return () => {
      if (gameRef.current) gameRef.current.dispose();
    };
  }, [mapName, soloMode, maxPlayers]);

  const isMenu = status === "menu";
  const mapDisplayName = gameRef.current?.mapInfo?.displayName ?? mapName;

  const handleEnterClick = (event) => {
    event.currentTarget.blur();
    if (gameRef.current?.sound) gameRef.current.sound.unlock();
    const container = containerRef.current;
    if (!container) return;
    try {
      const result = container.requestPointerLock();
      if (result && typeof result.catch === "function") result.catch(() => {});
    } catch (error) {
      console.warn("Pointer lock no disponible:", error);
    }
  };

  return (
    <Box sx={{ position: "relative", width: "100%", height, overflow: "hidden" }}>
      <Box ref={containerRef} sx={{ position: "absolute", inset: 0 }} />

      <Box
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          transition: "box-shadow 0.4s ease",
          boxShadow: bloomingFlash
            ? "inset 0 0 140px rgba(247, 130, 180, 0.55)"
            : `inset 0 0 ${40 + hud.bloom * 0.6}px rgba(247, 130, 180, ${
                hud.bloom / BLOOM_MAX / 2.2
              })`,
        }}
      />

      {status === "playing" && (
        <>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              width: 5,
              height: 5,
              borderRadius: "50%",
              bgcolor: "rgba(255,255,255,0.9)",
              transform: "translate(-50%, -50%)",
              pointerEvents: "none",
              boxShadow: "0 0 6px rgba(0,0,0,0.6)",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: 18,
              left: 22,
              width: 210,
              pointerEvents: "none",
            }}
          >
            <Typography variant="caption" sx={{ color: "#f7a8c4", letterSpacing: 2 }}>
              FLOREO
            </Typography>
            <LinearProgress
              variant="determinate"
              value={hud.bloom}
              sx={{
                mt: 0.5,
                height: 8,
                borderRadius: 4,
                backgroundColor: "rgba(255,255,255,0.12)",
                "& .MuiLinearProgress-bar": {
                  borderRadius: 4,
                  background:
                    "linear-gradient(90deg, #c3a6ff 0%, #f7a8c4 60%, #ff8fab 100%)",
                },
              }}
            />
          </Box>
          <Box sx={{ position: "absolute", bottom: 16, right: 22, textAlign: "right" }}>
            <Typography
              variant="h6"
              sx={{
                color: "rgba(255,255,255,0.92)",
                fontWeight: 300,
                letterSpacing: 3,
                fontFamily: "monospace",
              }}
            >
              {hud.reloading ? "RECARGANDO…" : `${hud.ammo} / ${MAG_SIZE}`}
            </Typography>
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.45)", letterSpacing: 2 }}>
              SEMILLAS
            </Typography>
          </Box>
          <Box sx={{ position: "absolute", top: 14, right: 22, textAlign: "right" }}>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.75)", letterSpacing: 1 }}>
              Rivales florecidos · {hud.score}
            </Typography>
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.4)", letterSpacing: 1 }}>
              semillas plantadas · {hud.flowers}
            </Typography>
          </Box>
          {!soloMode && (
            <Box sx={{ position: "absolute", top: 14, left: 22 }}>
              <Typography variant="caption" sx={{ color: "rgba(195,166,255,0.8)", letterSpacing: 1 }}>
                multiplayer próximamente — modo solo activo
              </Typography>
            </Box>
          )}
        </>
      )}

      {status !== "playing" && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "rgba(13, 13, 26, 0.72)",
            backdropFilter: "blur(6px)",
          }}
        >
          <Box sx={{ textAlign: "center", px: 3, maxWidth: 480 }}>
            <Typography
              variant="h2"
              component="h1"
              sx={{
                color: "white",
                fontWeight: 300,
                letterSpacing: "0.35em",
                fontSize: { xs: "2rem", sm: "3rem" },
                mb: 1,
              }}
            >
              SEMILLAS
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.55)", mb: 4, letterSpacing: 1 }}>
              un shooter contemplativo en el jardín zen
              {!isMenu && " — pausa"}
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "auto auto",
                gap: "6px 18px",
                justifyContent: "center",
                textAlign: "left",
                mb: 4,
              }}
            >
              {[
                ["WASD", "moverse"],
                ["ratón", "apuntar"],
                ["click izq.", "disparar semillas"],
                ["shift", "esprintar"],
                ["espacio", "saltar"],
                ["R", "recargar"],
                ["ESC", "pausa"],
              ].map(([key, label]) => (
                <React.Fragment key={key}>
                  <Typography
                    variant="caption"
                    sx={{ color: "#c3a6ff", fontFamily: "monospace", letterSpacing: 1 }}
                  >
                    {key}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.65)" }}>
                    {label}
                  </Typography>
                </React.Fragment>
              ))}
            </Box>

            <Button
              variant="outlined"
              size="large"
              onClick={handleEnterClick}
              sx={{
                color: "white",
                borderColor: "rgba(255,255,255,0.5)",
                letterSpacing: 3,
                "&:hover": {
                  borderColor: "#f7a8c4",
                  backgroundColor: "rgba(247,168,196,0.12)",
                },
              }}
            >
              {isMenu ? "ENTRAR AL JARDÍN" : "CONTINUAR"}
            </Button>

            <Typography variant="caption" sx={{ display: "block", mt: 2, color: "rgba(255,255,255,0.3)" }}>
              mapa: {mapDisplayName} · bots: {Math.max(1, Math.min(3, maxPlayers - 1))} · si el click no
              responde esperá un segundo y volvé a intentar
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default FlowerShooterGame;
